/**
 * Programme ambassadeur (04/11/2026) — côté formateur : ses liens, ses filleuls, ses recommandations, ses gains, son
 * relevé ; et la page PUBLIQUE de recommandation d'un apprenant (`/recommandation/CODE`, sans compte).
 */
import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, ExternalLink, Handshake, Link2, Lock, UserPlus, Wallet } from "lucide-react";
import type { lirePageRecommandation, vueEspaceAmbassadeur } from "@/serveur/services/ambassadeurs";
import { api, dateFr, euros, ErreurApi, type Json } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, EtatVide, Etiquette, Indicateur, TitrePage, ZoneTexte, classesLienBouton } from "../ui/base";
import { Coquille } from "./Acces";

export type VueAmbassadeur = Json<Awaited<ReturnType<typeof vueEspaceAmbassadeur>>>;
export type GainVue = VueAmbassadeur["gains"][number];
type PageReco = Json<Awaited<ReturnType<typeof lirePageRecommandation>>>;

export const TON_STATUT: Record<GainVue["statut"], "neutre" | "accent" | "attente" | "danger"> = { en_attente: "neutre", acquis: "attente", valide: "accent", paye: "accent", annule: "danger" };

function LienACopier({ libelle, aide, valeur }: { libelle: string; aide: string; valeur: string }) {
  const [copie, setCopie] = useState(false);
  return (
    <div>
      <p className="text-sm font-medium text-encre">{libelle}</p>
      <p className="text-[13px] text-encre-3">{aide}</p>
      <div className="mt-1.5 flex gap-2">
        <input readOnly value={valeur} aria-label={libelle} className="chiffres min-w-0 flex-1 rounded-md border border-trait bg-papier-2 px-3 py-2 text-[13px] text-encre-2" onFocus={(e) => e.currentTarget.select()} />
        <Bouton
          taille="sm"
          icone={copie ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
          onClick={() => {
            void navigator.clipboard?.writeText(valeur).then(() => {
              setCopie(true);
              setTimeout(() => setCopie(false), 2000);
            });
          }}
        >
          {copie ? "Copié" : "Copier"}
        </Bouton>
      </div>
    </div>
  );
}

export function EspaceAmbassadeur() {
  const requetes = useQueryClient();
  const vue = useQuery({ queryKey: ["ambassadeur"], queryFn: () => api.get<VueAmbassadeur>("/ambassadeur") });
  const retourStripe = typeof location !== "undefined" ? new URLSearchParams(location.search).get("stripe") : null;
  const rafraichir = useMutation({ mutationFn: () => api.post("/ambassadeur/stripe/rafraichir"), onSettled: () => requetes.invalidateQueries({ queryKey: ["ambassadeur"] }) });
  const onboarding = useMutation({ mutationFn: () => api.post<{ url: string }>("/ambassadeur/stripe/onboarding"), onSuccess: (r) => location.assign(r.url) });
  useEffect(() => {
    // Stripe Connect en sommeil : sans effet tant que le serveur ne le propose pas (v.stripe nul).
    if (retourStripe === "retour" && vue.data?.stripe) rafraichir.mutate();
  }, [retourStripe, vue.data?.stripe]);

  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const v = vue.data;
  return (
    <>
      <TitrePage
        titre="Programme ambassadeur"
        soustitre={`Faites connaître la plateforme : ${euros(v.regles.prime_parrainage_centimes)} pour chaque formateur parrainé (à son premier dossier) et ${String(v.regles.taux_recommandation_pourcent).replace(".", ",")} % du montant HT de la formation d'un apprenant que vous recommandez, une fois la formation réalisée.`}
        actions={
          v.actif && (
            <a href="/api/ambassadeur/releve.csv" className={classesLienBouton("secondaire")} download>
              <Download className="size-4" aria-hidden /> Relevé des gains validés (CSV)
            </a>
          )
        }
      />
      <Alerte ton="info" titre="Formations financées par le CPF">
        Le programme ne s'applique pas aux formations financées par le CPF (Mon Compte Formation) : aucune rémunération n'est versée pour un tel dossier, ni prime de parrainage, ni commission de recommandation.
      </Alerte>

      {!v.actif ? (
        <div className="mt-6">
          <EtatVide icone={<Lock className="size-8" aria-hidden />} titre="Votre programme s'active après votre première session facturée">
            Dès qu'une première session aura été facturée par l'organisme, vous trouverez ici votre code, votre lien de parrainage et votre formulaire de recommandation.
          </EtatVide>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicateur libelle="En attente" valeur={v.totaux.en_attente.nombre} detail="filleul inscrit, dossier en cours" icone={<Handshake className="size-4" aria-hidden />} />
            <Indicateur libelle="Acquis" valeur={euros(v.totaux.acquis.montant)} detail={`${v.totaux.acquis.nombre} gain(s), en cours de validation`} ton="attente" icone={<Wallet className="size-4" aria-hidden />} />
            <Indicateur libelle="Validés, à facturer" valeur={euros(v.totaux.valide.montant)} detail={`${v.totaux.valide.nombre} gain(s)`} ton="accent" icone={<Check className="size-4" aria-hidden />} />
            <Indicateur libelle="Total payé" valeur={euros(v.total_paye)} detail={`${v.totaux.paye.nombre} paiement(s)`} ton="accent" icone={<Wallet className="size-4" aria-hidden />} />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Carte className="space-y-4 p-5">
              <div className="flex items-center gap-2">
                <Link2 className="size-4 text-accent" aria-hidden />
                <h2 className="text-[15px] font-semibold">Vos liens</h2>
                <Etiquette ton="accent">Code {v.code}</Etiquette>
              </div>
              <LienACopier libelle="Parrainer un formateur" aide="Le formateur s'inscrit depuis ce lien : son compte vous est rattaché." valeur={v.liens!.parrainage_vitrine} />
              <LienACopier libelle="Lien direct vers l'inscription" aide="Même effet, sans passer par le site public." valeur={v.liens!.parrainage_direct} />
              <LienACopier libelle="Recommander un apprenant" aide="La personne laisse ses coordonnées et son besoin ; l'organisme la recontacte." valeur={v.liens!.recommandation} />
            </Carte>
            <Carte className="space-y-3 p-5">
              <h2 className="text-[15px] font-semibold">Être payé</h2>
              {v.stripe?.propose ? (
                <>
                  <p className="text-sm text-encre-2">L'organisme règle vos gains validés par Stripe : connectez un compte (inscription hébergée par Stripe, quelques minutes, pièce d'identité et IBAN).</p>
                  {v.stripe.mode === "test" && <Etiquette ton="attente">Stripe en mode test : aucun argent réel</Etiquette>}
                  {v.stripe.virements_actifs ? (
                    <Alerte ton="succes">Compte Stripe connecté : il peut recevoir vos paiements.</Alerte>
                  ) : (
                    <>
                      {v.stripe.compte_connecte && <Alerte ton="attention">Inscription Stripe à terminer : Stripe attend encore des informations.</Alerte>}
                      <Bouton variante="primaire" icone={<ExternalLink className="size-4" aria-hidden />} enCours={onboarding.isPending} onClick={() => onboarding.mutate()}>
                        {v.stripe.compte_connecte ? "Reprendre mon inscription Stripe" : "Connecter mon compte Stripe"}
                      </Bouton>
                    </>
                  )}
                  {onboarding.error && <Alerte ton="danger">{onboarding.error.message}</Alerte>}
                </>
              ) : (
                <p className="text-sm text-encre-2">Vos gains validés vous sont réglés par virement. Téléchargez le relevé des gains validés pour établir votre facture à l'organisme.</p>
              )}
            </Carte>
          </div>
        </>
      )}

      <section className="mt-8">
        <h2 className="mb-3 text-[17px] font-semibold">Vos gains</h2>
        {v.gains.length === 0 ? (
          <p className="text-sm text-encre-3">Aucun gain pour l'instant.</p>
        ) : (
          <TableGains gains={v.gains} />
        )}
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 text-[17px] font-semibold">Vos filleuls</h2>
          {v.filleuls.length === 0 ? (
            <p className="text-sm text-encre-3">Aucun formateur parrainé.</p>
          ) : (
            <ul className="divide-y divide-trait rounded-lg border border-trait bg-carte">
              {v.filleuls.map((f, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2"><UserPlus className="size-4 text-encre-3" aria-hidden /> {f.nom}</span>
                  <span className="text-encre-3">inscrit le {dateFr(f.inscrit_le)} {f.gain && <Etiquette>{f.gain}</Etiquette>}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h2 className="mb-3 text-[17px] font-semibold">Vos recommandations</h2>
          {v.recommandations.length === 0 ? (
            <p className="text-sm text-encre-3">Aucune recommandation reçue.</p>
          ) : (
            <ul className="divide-y divide-trait rounded-lg border border-trait bg-carte">
              {v.recommandations.map((r) => (
                <li key={r.numero} className="px-4 py-2.5 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span>{r.apprenant} <span className="chiffres text-encre-3">· {r.numero}</span></span>
                    <Etiquette ton={r.statut === "convertie" ? "accent" : r.statut === "sans_suite" ? "danger" : "neutre"}>{LIBELLES_RECO[r.statut]}</Etiquette>
                  </div>
                  {r.motif && <p className="mt-1 text-[12.5px] text-encre-3">{r.motif}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

export const LIBELLES_RECO: Record<VueAmbassadeur["recommandations"][number]["statut"], string> = { nouvelle: "Reçue", transformee: "Prise en charge", convertie: "Dossier ouvert", sans_suite: "Sans suite" };

export function TableGains({ gains, actions }: { gains: ReadonlyArray<GainVue & { ambassadeur?: string }>; actions?: (g: GainVue) => React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-trait bg-carte">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-papier-2 text-left text-[12.5px] text-encre-3">
          <tr>
            {actions && <th className="px-3 py-2 font-medium">Ambassadeur</th>}
            <th className="px-3 py-2 font-medium">Nature</th>
            <th className="px-3 py-2 font-medium">Origine</th>
            <th className="px-3 py-2 font-medium">Dossier</th>
            <th className="px-3 py-2 text-right font-medium">Montant</th>
            <th className="px-3 py-2 font-medium">Statut</th>
            <th className="px-3 py-2 font-medium">Paiement</th>
            {actions && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-trait">
          {gains.map((g) => (
            <tr key={g.id} className="align-top">
              {actions && <td className="px-3 py-2.5 font-medium">{g.ambassadeur}</td>}
              <td className="px-3 py-2.5">{g.nature_libelle}</td>
              <td className="px-3 py-2.5 text-encre-2">{g.nature === "parrainage" ? g.filleul : g.recommandation}</td>
              <td className="chiffres px-3 py-2.5 text-encre-2">{g.dossier_reference || "—"}</td>
              <td className="chiffres px-3 py-2.5 text-right">
                {g.montant_centimes !== null ? euros(g.montant_centimes) : <span className="text-encre-3">{g.montant_prevu !== null ? `${euros(g.montant_prevu)} prévus` : "4 % à la fin"}</span>}
                {g.base_centimes !== null && <div className="text-[12px] text-encre-3">{String(g.taux_pourcent).replace(".", ",")} % de {euros(g.base_centimes)}</div>}
              </td>
              <td className="px-3 py-2.5">
                <Etiquette ton={TON_STATUT[g.statut]}>{g.statut_libelle}</Etiquette>
                {g.motif_annulation && <div className="mt-1 max-w-[28ch] text-[12px] text-encre-3">{g.motif_annulation}</div>}
                {g.stripe_statut === "annule_chez_stripe" && <div className="mt-1 text-[12px] text-danger-encre">Transfert annulé chez Stripe</div>}
              </td>
              <td className="px-3 py-2.5 text-[12.5px] text-encre-2">{g.statut === "paye" ? `${g.moyen_paiement === "stripe" ? "Stripe" : "Virement"} · ${dateFr(g.date_paiement)} · ${g.reference_paiement}` : "—"}</td>
              {actions && <td className="px-3 py-2.5 text-right whitespace-nowrap">{actions(g)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Page publique : un prospect apprenant laisse ses coordonnées et son besoin, avec son consentement. */
export function PageRecommandation() {
  const { code } = useParams({ from: "/recommandation/$code" });
  const page = useQuery({ queryKey: ["recommandation", code], queryFn: () => api.get<PageReco>(`/public/recommandation/${code}`), retry: false });
  const [v, setV] = useState({ prenom: "", nom: "", email: "", telephone: "", besoin: "", consentement: false, site_web: "" });
  const envoi = useMutation({ mutationFn: () => api.post<{ numero: string }>(`/public/recommandation/${code}`, { ...v, consentement_version: page.data?.consentement.version }) });
  const err = envoi.error instanceof ErreurApi ? (envoi.error.details?.champs ?? {}) : {};
  const champ = (cle: "prenom" | "nom" | "email" | "telephone" | "besoin") => ({ value: v[cle], erreur: err[cle], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV({ ...v, [cle]: e.target.value }) });
  if (page.isPending) return <Chargement />;
  if (page.error)
    return (
      <Coquille titre="Lien indisponible" accroche="Ce lien de recommandation n'est pas (ou plus) valable.">
        <Alerte ton="attention">Demandez à la personne qui vous l'a transmis de vous renvoyer son lien.</Alerte>
      </Coquille>
    );
  const p = page.data;
  return (
    <Coquille titre={`Votre projet de formation — ${p.organisme}`} accroche={`${p.ambassadeur_prenom} pense que ${p.organisme} peut vous accompagner. Décrivez votre besoin : l'organisme vous recontacte.`}>
      {envoi.isSuccess ? (
        <Alerte ton="succes" titre="Demande transmise">
          Merci ! Votre demande (n° {envoi.data.numero}) a bien été transmise. L'organisme vous recontacte rapidement.
        </Alerte>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); envoi.mutate(); }}>
          <div className="grid grid-cols-2 gap-3">
            <Champ libelle="Prénom" autoComplete="given-name" required {...champ("prenom")} />
            <Champ libelle="Nom" autoComplete="family-name" required {...champ("nom")} />
          </div>
          <Champ libelle="Adresse e-mail" type="email" autoComplete="email" required {...champ("email")} />
          <Champ libelle="Téléphone (facultatif)" type="tel" autoComplete="tel" {...champ("telephone")} />
          <ZoneTexte libelle="Votre besoin" rows={4} required placeholder="Ex. : une formation en bureautique pour mon nouveau poste, en mars, à distance." {...champ("besoin")} />
          {/* Champ piège : invisible pour une personne, rempli par les robots. */}
          <input type="text" name="site_web" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" value={v.site_web} onChange={(e) => setV({ ...v, site_web: e.target.value })} />
          <p className="rounded-md bg-papier-2 px-3 py-2 text-[12.5px] leading-relaxed text-encre-2">{p.mention_cpf}</p>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" required className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]" checked={v.consentement} onChange={(e) => setV({ ...v, consentement: e.target.checked })} />
            <span className="text-encre-2">{p.consentement.texte}</span>
          </label>
          {envoi.error && <Alerte ton="danger">{envoi.error.message}</Alerte>}
          <Bouton type="submit" variante="primaire" className="w-full" enCours={envoi.isPending} disabled={!v.consentement}>
            Transmettre ma demande
          </Bouton>
        </form>
      )}
    </Coquille>
  );
}
